import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faArrowsToDottedLine } from '@fortawesome/pro-regular-svg-icons/faArrowsToDottedLine';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Space,
  theme,
  Typography,
} from 'antd';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { createFormFields } from 'pos-client/components/shared/Form/FormUtils.ts';
import { PosFormItem } from 'pos-client/components/shared/Form/PosFormItem.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { useSettingGroupsWithSettings } from 'pos-client/hooks/useSettings.tsx';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  ServiceLocationConfigurationSchemaV2,
  type ServiceLocationConfigurationV2Model,
} from 'pos-shared/models/locationSchema.ts';
import type { SettingTable } from 'pos-shared/models/settingsSchema';

interface CreateEditLocationIntegrationModalProps {
  handleEditSelectedIntegration: (
    integration: ServiceLocationConfigurationV2Model | null
  ) => void;
  selectedIntegration?: ServiceLocationConfigurationV2Model | null;
  isEdit: boolean;
  isModalOpen: boolean;
  title: string;
}

type IntegrationOption = {
  key: string;
  label: string;
  name: string;
  value: string;
  globalSettingId: string;
  description: string | null | undefined;
  modifiedDate?: string | null;
};

export const extractNonGroupItems = (items: SettingTable[]): SettingTable[] => {
  const result: SettingTable[] = [];
  const serviceLocationIntegrationGroup = items.find(
    (item) => item.name === 'Service Location'
  );

  const traverse = (array: SettingTable[]): void => {
    array.forEach((item) => {
      if (!item?.settings?.length) {
        result.push(item);
      }

      if (item.settings && item.settings.length > 0) {
        traverse(item.settings);
      }
    });
  };

  traverse(serviceLocationIntegrationGroup?.groups || []);
  return result;
};

export const CreateEditLocationIntegrationModal = ({
  handleEditSelectedIntegration,
  selectedIntegration,
  isEdit,
  isModalOpen,
  title,
}: CreateEditLocationIntegrationModalProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const trpcContext = trpc.useUtils();
  const { locationId } = useParams<'locationId'>();
  const [newConfigError, setNewConfigError] = useState<null | string>(null);

  const [selectedOption, setSelectedOption] = useState<IntegrationOption>();
  const { showSuccess, showError } = useNotifications();

  const { settings, isLoadingSettings } = useSettingGroupsWithSettings({
    name: 'Integration',
    pageNumber: 1,
    resultsPerPage: 50,
  });

  const nonGroupItems = useMemo(
    () => extractNonGroupItems(settings),
    [settings]
  );
  const integrationOptions = nonGroupItems.map(
    ({ name, value, id, description, modifiedDate }) => ({
      key: id,
      label: name,
      name: name,
      value: value,
      globalSettingId: id,
      description: description,
      modifiedDate: modifiedDate,
    })
  );

  /******************************************** REQUESTS **************************************************/

  const { data: locationConfigurations } =
    trpc.serviceLocation.getServiceLocationConfigV2.useQuery(
      {
        listOfServiceLocationIds: [locationId as string],
      },
      {
        enabled: !!locationId,
      }
    );

  const { mutate: createConfig, isLoading: isCreatingConfig } =
    trpc.serviceLocation.createServiceLocationConfigV2.useMutation({
      onSuccess: async () => {
        await trpcContext.serviceLocation.getServiceLocationConfigV2.invalidate(
          {
            listOfServiceLocationIds: [locationId as string],
          }
        );
        showSuccess(
          t('common.notification.success.message', {
            action: 'created',
            entity: 'configuration',
          })
        );
        handleReset();
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'created',
            entity: 'configuration',
          })
        );
      },
    });

  const { mutate: updateConfig, isLoading: isUpdatingConfig } =
    trpc.serviceLocation.updateServiceLocationConfigV2.useMutation({
      onSuccess: async () => {
        await trpcContext.serviceLocation.getServiceLocationConfigV2.invalidate(
          {
            listOfServiceLocationIds: [locationId as string],
          }
        );
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'configuration',
          })
        );
        handleReset();
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'updated',
            entity: 'configuration',
          })
        );
      },
    });

  const { mutateAsync: deleteConfig, isLoading: isDeletingConfig } =
    trpc.serviceLocation.deleteServiceLocationConfigV2.useMutation({
      onSuccess: async () => {
        await trpcContext.serviceLocation.getServiceLocationConfigV2.invalidate(
          {
            listOfServiceLocationIds: [locationId as string],
          }
        );
        showSuccess(
          t('common.notification.success.message', {
            action: 'deleted',
            entity: 'configuration',
          })
        );
        handleReset();
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'deleted',
            entity: 'configuration',
          })
        );
      },
    });

  /****************************************^ REQUESTS ^********************************************/

  const { showModal: confirmDeleteConfiguration } = useConfirmModal({
    type: 'delete',
    title: t('locations.config.deleteConfiguration'),
    content: t('locations.config.configurationDeleteConfirmationMessage'),
    isLoading: isDeletingConfig,
    confirmText: t('common.buttons.delete'),
    confirmProps: {
      disabled: isDeletingConfig,
      ghost: true,
    },
  });

  const confirmDeleteModal = (configId: string) => {
    confirmDeleteConfiguration({
      onConfirm: async () => deleteConfig({ configurationId: configId }),
    });
  };

  const addConfiguration = async () => {
    const locationConfig = locationConfigurations?.results ?? [];

    const keyAlreadyExists = locationConfig.find((config) => {
      if (config.name.toLowerCase() === selectedOption?.label.toLowerCase()) {
        return true;
      }
    });

    try {
      if (keyAlreadyExists) {
        setNewConfigError(t('locations.config.existingConfig'));
      } else {
        createConfig({
          serviceLocationId: locationId as string,
          name: selectedOption?.label || '',
          value: formValues?.value || '',
          globalSettingId: selectedOption?.globalSettingId || '',
        });

        setNewConfigError(null);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  };

  const handleEdit = () => {
    const updatedIntegration = {
      id: selectedIntegration?.id as string,
      value: formValues.value,
    };
    updateConfig(updatedIntegration);
  };

  const handleCreate = () => {
    addConfiguration();
  };
  const handleReset = () => {
    handleEditSelectedIntegration(null);
  };

  const handleSelectedOptionChange = (
    selectedIntegration:
      | {
          label: string;
          value: string;
        }
      | undefined
  ) => {
    const selectedIntegrationOption = integrationOptions.find(
      (option) => option.label === selectedIntegration?.label
    );
    setSelectedOption(selectedIntegrationOption as IntegrationOption);
  };

  const form = useForm<ServiceLocationConfigurationV2Model>({
    mode: 'all',
    shouldFocusError: true,
    resolver: zodResolver(ServiceLocationConfigurationSchemaV2),
    defaultValues: {
      name: isEdit ? selectedIntegration?.name : undefined,
      description: isEdit ? selectedIntegration?.description : undefined,
      value: isEdit ? selectedIntegration?.value : undefined,
      dateUpdated: isEdit ? selectedIntegration?.dateUpdated : undefined,
      globalSettingId: isEdit ? selectedIntegration?.id : undefined,
    },
  });

  const formValues = form.getValues();

  const selectedIntegrationOption = nonGroupItems.find(
    (item) => item.name === formValues.name
  );

  const formFields = createFormFields(form);

  const settingsItem = nonGroupItems.find(
    (item) => item.name === selectedIntegration?.name
  );

  return (
    <PosModal
      header={{ title, icon: faArrowsToDottedLine }}
      open={isModalOpen}
      onCancel={handleReset}
      footer={
        <Flex justify="space-between" style={fullWidth}>
          <Button
            danger
            hidden={!isEdit}
            onClick={() =>
              confirmDeleteModal(selectedIntegration?.id as string)
            }
            style={{ marginLeft: 'auto' }}
            loading={isDeletingConfig}
          >
            {t('common.buttons.remove')}
          </Button>
          <Space style={{ flexGrow: 1, justifyContent: 'flex-end' }}>
            <Button type="default" onClick={handleReset}>
              {t('common.buttons.cancel')}
            </Button>

            <Button
              key="submit"
              type="primary"
              onClick={isEdit ? handleEdit : handleCreate}
              loading={isCreatingConfig || isUpdatingConfig}
              disabled={!form.formState.isDirty}
            >
              {isEdit ? t('common.buttons.save') : t('common.buttons.create')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        fields={formFields}
        layout="vertical"
        style={{
          marginTop: token.marginMD,
          display: 'flex',
          flexDirection: 'column',
          gap: token.margin,
        }}
      >
        <Flex vertical gap={token.margin}>
          <PosFormItem
            label={t('locations.integrations.integrationNameLabel')}
            control={form.control}
            name="name"
            boldLabel
            formItemProps={{ id: 'integrationNameDropdown' }}
          >
            <Select
              placeholder={t(
                'locations.integrations.integrationsDropdownPlaceholder'
              )}
              options={integrationOptions.map((option) => ({
                label: option.label,
                value: option.name,
              }))}
              loading={isLoadingSettings}
              aria-label={t(
                'locations.integrations.integrationsDropdownPlaceholder'
              )}
              onChange={(_, option) => {
                const selected = Array.isArray(option) ? option[0] : option;
                handleSelectedOptionChange(
                  selected as { label: string; value: string } | undefined
                );
              }}
            />
          </PosFormItem>

          <PosFormItem
            boldLabel
            label={t('common.words.value')}
            control={form.control}
            name="value"
          >
            {settingsItem &&
            settingsItem?.type?.toLowerCase() === 'password' ? (
              <Input.Password
                placeholder={t('common.words.value')}
                aria-label={t('common.words.value')}
                value={
                  isEdit ? selectedIntegration?.value : selectedOption?.value
                }
                onChange={(e) => form.setValue('value', e.target.value)}
              />
            ) : (
              <Input
                placeholder={t('common.words.value')}
                aria-label={t('common.words.value')}
                value={
                  isEdit ? selectedIntegration?.value : selectedOption?.value
                }
                onChange={(e) => form.setValue('value', e.target.value)}
              />
            )}
          </PosFormItem>

          <PosFormItem
            label={t('common.audit.dateModified')}
            control={form.control}
            name="dateUpdated"
            boldLabel
          >
            <Typography.Text style={{ marginRight: token.marginXS }}>
              {isEdit
                ? convertISOStringToLocalDate(
                    selectedIntegration?.dateUpdated.toString()
                  )?.toString()
                : convertISOStringToLocalDate(
                    selectedOption?.modifiedDate?.toString()
                  ) || '-'}
            </Typography.Text>
          </PosFormItem>

          <PosFormItem
            label={t('common.words.description')}
            control={form.control}
            name="description"
            boldLabel
          >
            <Typography.Text style={{ marginRight: token.marginXS }}>
              {isEdit
                ? selectedIntegrationOption?.description || '-'
                : selectedOption?.description || '-'}
            </Typography.Text>
          </PosFormItem>
        </Flex>
        {newConfigError && (
          <Row
            style={{
              marginTop: token.marginSM,
              marginBottom: token.marginSM,
            }}
            align="middle"
          >
            <Col>
              <Typography.Text type="danger">{newConfigError}</Typography.Text>
            </Col>
          </Row>
        )}
      </Form>
    </PosModal>
  );
};
